import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormDropzone from "../../shared/dropzone";
import { multipartFormData } from "../../helpers/api.services";
import { BlogService } from "./service";
import { BLOG_URL } from "../../helpers/api.routes";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
  title: yup.string().required('formErrors.titleRequired'),
  intro: yup.string(),
  content: yup.string(),
  category: yup.string().nullable().optional(),
  picture: yup.mixed()
});

interface BlogState {
  title: string;
  intro?: string;
  content?: string;
  category?: string | null;
  pictures?: {
    path?: string;
  };
}

const BlogForm: React.FC = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { control, handleSubmit, setValue, getValues, reset, formState: { errors } } = useForm<BlogState>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      intro: "",
      content: "",
      category: null,
      pictures: { path: "" },
    }
  });

  useEffect(() => {
    if (isEdit) {
      const fetchBlog = async () => {
        try {
          const response = await BlogService.getSingle(+id!);
          const blog = response;
          reset({
            title: blog.title,
            intro: blog.intro,
            content: blog.content ? blog.content : "",
            category: blog.category ?? null,
            pictures: blog.pictures[0], // Assuming the first picture is the main one
          });
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchBlog();
    }
  }, [isEdit, id, reset]);

  const handleFileUpload = (files: any) => {
    setValue("pictures", files[0]);
  };

  const onSubmit = async (data: BlogState) => {
    const url = isEdit ? `${BLOG_URL}/${id}` : BLOG_URL;
    const method = isEdit ? "put" : "post";

    multipartFormData(
      data.pictures,
      url,
      JSON.stringify(data),
      method,
      'file'
    )
      .then(() => {
        SuccessToast(t(`blog.${isEdit ? 'successUpdate' : 'successCreate'}`));
        navigate('/blogs');
        reset();
      })
      .catch((error) => ErrorToast(t(error)));
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">{t("blog.title")}</label>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                            placeholder={t("blog.enterTitle")}
                            {...field}
                          />
                        )}
                      />
                      {errors.title && <div className="invalid-feedback">
                        {t(errors.title.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">{t("blog.category")}</label>
                      <Controller
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <select
                            className="form-select"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value || null)}
                          >
                            <option value="">{t("blog.selectCategory")}</option>
                            <option value="blog">{t("blog.categoryBlog")}</option>
                            <option value="podcast">{t("blog.categoryPodcast")}</option>
                          </select>
                        )}
                      />
                    </div>
                  </Col>
                  <Col sm={12} xl={12}>
                    <div className="mb-0">
                      <label className="form-label">{t("blog.intro")}</label>
                      <Controller
                        name="intro"
                        control={control}
                        render={({ field }) => (
                          <textarea
                            className={`form-control ${errors.intro ? 'is-invalid' : ''}`}
                            placeholder={t("blog.enterIntro")}
                            {...field}
                          />
                        )}
                      />
                      {errors.intro && <div className="invalid-feedback">
                        {t(errors.intro.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">{t("blog.content")}</label>
                      <Controller
                        name="content"
                        control={control}
                        render={({ field }) => (
                          <Editor
                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                            value={field.value}
                            onEditorChange={(content: string) => field.onChange(content)}
                            init={{
                              height: 360,
                              menubar: false,
                              branding: false,
                              plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'table', 'help', 'wordcount',
                              ],
                              toolbar:
                                'undo redo | blocks | bold italic underline strikethrough | ' +
                                'alignleft aligncenter alignright alignjustify | ' +
                                'bullist numlist outdent indent | table | link image | removeformat | code',
                              content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }',
                            }}
                          />
                        )}
                      />
                      {errors.content && <div className="invalid-feedback">
                        {t(errors.content.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <FormDropzone onFileUpload={handleFileUpload} existingFiles={[(getValues('pictures') as any)?.path]} />
                    {errors.pictures && <div className="invalid-feedback d-block">{t("blog.pictureRequired")}</div>}
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`blog.${isEdit ? 'update' : 'create'}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default BlogForm;